import React, { BaseSyntheticEvent, useState } from "react";

import {
  MdmObjectDto,
  MdmObjectNoteDto,
  useCreateNoteMutation,
  useDeleteNoteMutation,
  useGetAllNotesQuery,
  useUpdateNoteMutation,
} from "gql/generated/types";
import { GET_NOTES } from "gql/note.operations";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import ReactQuill from "react-quill-new";
import { confirmPopup } from "primereact/confirmpopup";
import { ProgressSpinner } from "primereact/progressspinner";
import { T } from "services/lang/LanguageService";
import { useTimezone } from "hooks/useTimezone";
import { reactQuillModules } from "utils/constants";
import { fmtDatetime } from "utils/time-utils";
import { apolloErrorToaster, successToaster } from "../utils/utils";
import "react-quill-new/dist/quill.snow.css";

type ObjectNotesProps = {
  object: MdmObjectDto;
};

const ObjectNotes: React.FC<ObjectNotesProps> = ({ object }) => {
  const [note, setNote] = useState<string | null>(null);
  const [noteData, setNoteData] = useState<MdmObjectNoteDto | null>(null);
  const [editable, setEditable] = useState(false);

  const [createNoteMutation, { loading: loadingCreate }] = useCreateNoteMutation({
    onCompleted: () => successToaster(T("alert.note-created-successfully")),
    onError: apolloErrorToaster,
  });

  const [updateNoteMutation, { loading: loadingUpdate }] = useUpdateNoteMutation({
    onCompleted: () => {
      successToaster(T("alert.note-updated-successfully"));
      cancelEdit();
    },
    onError: apolloErrorToaster,
  });

  const { data, loading } = useGetAllNotesQuery({
    variables: {
      id: object.id!,
    },
    skip: !object.id,
  });

  const handleNoteCreate = () => {
    if (!note) return;
    createNoteMutation({
      variables: {
        objectId: object.id!,
        note,
      },
      update: (cache, { data: qData }) => {
        cache.updateQuery({ query: GET_NOTES, variables: { id: object.id } }, oldData =>
          oldData
            ? {
                allNotesByObjectId: [...oldData.allNotesByObjectId, qData?.createNote],
              }
            : null
        );
        cache.modify({
          id: cache.identify({ id: object.id, __typename: "MdmObjectDTO" }),
          fields: {
            info: info => {
              return {
                ...info,
                notesCount: info.notesCount + 1,
              };
            },
          },
        });
      },
    });
    setNote(null);
  };

  const handleEditClick = (objectNote: MdmObjectNoteDto) => {
    setNote(objectNote.note!);
    setNoteData(objectNote);
    setEditable(true);
  };

  const handleNoteUpdate = () => {
    const { createdByUsername, modifiedByUsername, ...restNote } = noteData!;
    updateNoteMutation({ variables: { note: { ...restNote, note } } });
  };

  const cancelEdit = () => {
    setNote(null);
    setEditable(false);
    setNoteData(null);
  };
  const notesResponse = data?.allNotesByObjectId ?? [];
  const mapNotes = [...notesResponse]
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map(n => <ObjectNote key={n.id} note={n} onEditNote={handleEditClick} objectId={object.id!} />);

  return (
    <div className="mx-auto" style={{ maxWidth: "800px" }}>
      {loading && <ProgressSpinner className="mx-auto w-full" />}
      {data && data.allNotesByObjectId.length > 0 && (
        <Card style={{ maxHeight: "500px", overflowY: "auto" }}>{mapNotes}</Card>
      )}
      <ReactQuill value={note ?? ""} onChange={e => setNote(e)} modules={reactQuillModules} />
      {editable ? (
        <div className="flex flex-row gap-3 mt-3">
          <Button
            type="button"
            label={T("action.update")}
            onClick={handleNoteUpdate}
            loading={loadingUpdate}
          />
          <Button
            type="button"
            label={T("word.cancel")}
            className="p-button-secondary"
            onClick={cancelEdit}
          />
        </div>
      ) : (
        <Button
          type="button"
          label={T("action.create")}
          onClick={handleNoteCreate}
          className="w-auto mt-3"
          loading={loadingCreate}
        />
      )}
    </div>
  );
};

export default ObjectNotes;

type ObjectNoteProps = {
  note: MdmObjectNoteDto;
  onEditNote: (note: MdmObjectNoteDto) => void;
  objectId: string;
};

const ObjectNote: React.FC<ObjectNoteProps> = ({ note, onEditNote, objectId }) => {
  const tz = useTimezone();
  const [deleteNoteMutation] = useDeleteNoteMutation({
    onCompleted: () => successToaster(T("alert.note-deleted-successfully")),
    onError: apolloErrorToaster,
  });

  const confirmOnDelete = (event: BaseSyntheticEvent) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-dialog.are-you-sure-you-want-to-delete"),
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-triangle",
      accept: () =>
        deleteNoteMutation({
          variables: {
            id: note.id,
          },
          update(cache) {
            const normalizedId = cache.identify({ id: note.id, __typename: "MdmObjectNoteDTO" });
            cache.evict({ id: normalizedId });
            cache.gc();
            cache.modify({
              id: cache.identify({ id: objectId, __typename: "MdmObjectDTO" }),
              fields: {
                info: info => {
                  return {
                    ...info,
                    notesCount: info.notesCount - 1,
                  };
                },
              },
            });
          },
        }),
    });
  };
  const updated = Math.abs(new Date(note.createdAt).getTime() - new Date(note.modifiedAt).getTime()) > 10;

  return (
    <div id="note" className="mb-3 border-1 border-300 border-round px-3 pb-2">
      <div className="flex justify-content-end align-items-center m-0">
        <span className="text-500">{note.createdByUsername}</span>
        <span className="text-500 ml-5 mr-auto">
          {fmtDatetime(note.createdAt, tz.display)}
          {updated && (
            <span
              title={`${T("word.edited-on")} ${fmtDatetime(note.modifiedAt, tz.display)}, ${T("mdm.edited-by")} ${note.modifiedByUsername}`}
            >
              &nbsp;({T("word.edited")})
            </span>
          )}
        </span>
        <Button
          type="button"
          icon="pi pi-pencil"
          title={T("action.edit")}
          className="p-button-rounded p-button-text p-0"
          onClick={() => onEditNote(note)}
        />
        <Button
          type="button"
          icon="pi pi-trash"
          title={T("action.delete")}
          className="p-button-rounded p-button-text p-button-danger p-0"
          onClick={confirmOnDelete}
        />
      </div>
      <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: note.note ?? "" }}
        style={{ wordBreak: "break-word" }}
      />
    </div>
  );
};
